import * as React from "react";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { LabelValue } from "components/LabelValue/LabelValue";
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Toolbar } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { LoadingButton } from "components/LoadingButton/LoadingButton";
import { CognitoStateSwitch } from "components/cognitoComponents/CognitoStateSwitch/CognitoStateSwitch";
import { EmailVerifiedSwitch } from "components/cognitoComponents/EmailVerifiedSwitch/EmailVerifiedSwitch";
import { formatEpoch } from "utils/dateUtils";
import { AdminRoleSwitch } from "./AdminRoleSwitch";
import { EditAttributesDialog } from "./EditAttributesDialog";
import { SetPasswordDialog } from "components/cognitoComponents/SetPasswordDialog/SetPasswordDialog";
import { SnackbarApiError } from "components/SnackbarApiError/SnackbarApiError";
import { useDialogState } from "hooks/useDialogState";
import { useSnackbarAlert } from "hooks/useSnackbarAlert";
import { useMutation } from "react-query";
import { ClientContactSummary, CognitoUser, CognitoUserPoolType, UserAccountsService, UserGroup } from "gen/clients";
import { useUserSession } from "hooks/useUserSession";
import { MfaEnabledSwitch } from "./MfaEnabledSwitch";
import { useClientReadOnly } from "contexts/ClientLockContext";

interface Props {
  cognitoUser: CognitoUser;
  clientContact: ClientContactSummary;
  onRefetch: () => void;
}

const CognitoAccountCard: React.FC<Props> = ({ cognitoUser, clientContact, onRefetch }) => {
  const isReadOnly = useClientReadOnly();
  const { roles } = useUserSession();
  const canImpersonate = React.useMemo(
    () => roles?.includes(UserGroup.ADMIN) || roles?.includes(UserGroup.SUPER_USER) || false,
    [roles]
  );
  const [isSetPasswordDialogOpen, openSetPasswordDialog, closeSetPasswordDialog] = useDialogState();
  const [isEditAttributesDialogOpen, openEditAttributesDialog, closeEditAttributesDialog] = useDialogState();
  const { snackbarAlert, openSnackbarAlert, closeSnackbarAlert } = useSnackbarAlert({
    autoHideDuration: 5000,
    vertical: "bottom",
    horizontal: "left"
  });

  const {
    mutate: confirmUserAccount,
    isLoading: isConfirmUserAccountLoading,
    error: confirmUserAccountError
  } = useMutation(UserAccountsService.confirmCognitoUserSignUp, { onSuccess: onRefetch });

  const onEditAttributesSuccess = React.useCallback(() => {
    closeEditAttributesDialog();
    openSnackbarAlert({ message: "Operation successful", severity: "success" });
    onRefetch();
  }, [closeEditAttributesDialog, openSnackbarAlert, onRefetch]);

  const onSetPasswordSuccess = React.useCallback(() => {
    closeSetPasswordDialog();
    openSnackbarAlert({ message: "Operation successful", severity: "success" });
    onRefetch();
  }, [closeSetPasswordDialog, openSnackbarAlert, onRefetch]);

  return (
    <>
      {snackbarAlert}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight="bold">
            {cognitoUser.username}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={1}>
            <Toolbar disableGutters={true} variant="dense">
              <Box sx={{ flexGrow: 1 }} />
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    closeSnackbarAlert();
                    openSetPasswordDialog();
                  }}
                  disabled={isReadOnly}
                >
                  Set Password
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    closeSnackbarAlert();
                    openEditAttributesDialog();
                  }}
                  disabled={isReadOnly}
                >
                  Edit Attributes
                </Button>
                <LoadingButton
                  variant="outlined"
                  size="small"
                  disabled={cognitoUser.userStatus !== "UNCONFIRMED" || isReadOnly}
                  onClick={() =>
                    confirmUserAccount({
                      userPool: CognitoUserPoolType.PORTAL_USER_POOL,
                      cognitoUsername: encodeURIComponent(cognitoUser.username)
                    })
                  }
                  isLoading={isConfirmUserAccountLoading}
                  type="button"
                >
                  Confirm User Account
                </LoadingButton>
                {canImpersonate && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() =>
                      window.open(
                        `${import.meta.env.VITE_PUBLIC_PORTAL_URL}/internalLogin/${cognitoUser.username}`,
                        "_blank"
                      )
                    }
                  >
                    Impersonate
                  </Button>
                )}
              </Stack>
            </Toolbar>
            <LabelValue label="Username" value={cognitoUser.username} />
            <LabelValue
              label="Enabled"
              value={
                <CognitoStateSwitch
                  userPool={CognitoUserPoolType.PORTAL_USER_POOL}
                  cognitoUser={cognitoUser}
                  onSuccess={onRefetch}
                  disabled={isReadOnly}
                />
              }
            />
            <LabelValue
              label="Email Verified"
              value={
                <EmailVerifiedSwitch
                  userPool={CognitoUserPoolType.PORTAL_USER_POOL}
                  cognitoUser={cognitoUser}
                  onSuccess={onRefetch}
                  disabled={isReadOnly}
                />
              }
            />
            <LabelValue
              label="MFA Enabled"
              value={<MfaEnabledSwitch cognitoUser={cognitoUser} onSuccess={onRefetch} disabled={isReadOnly} />}
            />
            {cognitoUser.userStatus && <LabelValue label="Status" value={cognitoUser.userStatus} />}
            {cognitoUser?.lastVisit && <LabelValue label="Last Visit" value={formatEpoch(cognitoUser?.lastVisit)} />}
            <LabelValue
              label="Attributes"
              verticalAlignment="top"
              value={
                <Stack spacing={1}>
                  <LabelValue
                    label="xtrfClientId"
                    value={
                      <Typography
                        sx={theme =>
                          cognitoUser.xtrfClientId !== `${clientContact.clientId}`
                            ? { color: theme.palette.error.main, fontWeight: "bold" }
                            : {}
                        }
                      >
                        {cognitoUser.xtrfClientId}
                      </Typography>
                    }
                  />
                  <LabelValue
                    label="xtrfClientContactId"
                    value={
                      <Typography
                        sx={theme =>
                          cognitoUser.xtrfClientContactId !== `${clientContact.id}`
                            ? { color: theme.palette.error.main, fontWeight: "bold" }
                            : {}
                        }
                      >
                        {cognitoUser.xtrfClientContactId}
                      </Typography>
                    }
                  />
                  {cognitoUser.firstName && (
                    <LabelValue
                      label="First Name"
                      value={
                        <Typography
                          sx={theme =>
                            cognitoUser.firstName !== `${clientContact.firstName}`
                              ? { color: theme.palette.error.main, fontWeight: "bold" }
                              : {}
                          }
                        >
                          {cognitoUser.firstName}
                        </Typography>
                      }
                    />
                  )}
                  {cognitoUser.lastName && (
                    <LabelValue
                      label="Last Name"
                      value={
                        <Typography
                          sx={theme =>
                            cognitoUser.lastName !== `${clientContact.lastName}`
                              ? { color: theme.palette.error.main, fontWeight: "bold" }
                              : {}
                          }
                        >
                          {cognitoUser.lastName}
                        </Typography>
                      }
                    />
                  )}
                  {cognitoUser.email && (
                    <LabelValue
                      label="Email"
                      value={
                        <Typography
                          sx={theme =>
                            cognitoUser.email !== `${clientContact.email}`
                              ? { color: theme.palette.error.main, fontWeight: "bold" }
                              : {}
                          }
                        >
                          {cognitoUser.email}
                        </Typography>
                      }
                    />
                  )}
                </Stack>
              }
            />
            <Stack spacing={1} mt={2}>
              <Typography variant="subtitle2">Permissions</Typography>
              <Stack direction="row">
                <LabelValue
                  label="Is Org Admin?"
                  value={<AdminRoleSwitch cognitoUser={cognitoUser} onSuccess={onRefetch} disabled={isReadOnly} />}
                />
              </Stack>
            </Stack>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {isEditAttributesDialogOpen && cognitoUser?.username && (
        <EditAttributesDialog
          username={cognitoUser.username}
          xtrfClientId={cognitoUser.xtrfClientId}
          xtrfClientContactId={cognitoUser.xtrfClientContactId}
          firstName={cognitoUser.firstName}
          lastName={cognitoUser.lastName}
          email={cognitoUser.email}
          onSuccess={onEditAttributesSuccess}
          onClose={closeEditAttributesDialog}
        />
      )}

      {isSetPasswordDialogOpen && cognitoUser?.username && (
        <SetPasswordDialog
          userPool={CognitoUserPoolType.PORTAL_USER_POOL}
          username={cognitoUser.username}
          onSuccess={onSetPasswordSuccess}
          onClose={closeSetPasswordDialog}
        />
      )}

      {confirmUserAccountError && <SnackbarApiError error={confirmUserAccountError} />}
    </>
  );
};

export { CognitoAccountCard };
